import {
  formatDistanceToNow,
  format,
  differenceInCalendarDays,
} from 'date-fns';
import { useQuotationActivities } from '../hooks';
import { formatQuoteMoney } from '../utils';

const ACTION_LABEL = {
  product_created: 'Product added',
  product_price_changed: 'Price changed',
  product_tax_changed: 'Tax changed',
  product_availability_changed: 'Availability changed',
  product_updated: 'Product updated',
  product_deleted: 'Product deleted',
  template_changed: 'Template changed',
  quotation_updated: 'Quotation updated',
};

/** Skip internal / noise keys in change list */
const HIDDEN_CHANGE_KEYS = new Set([
  'product_id',
  'id',
  'quotation',
  'company',
]);

function formatTimeAgo(dateString) {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    const diffDays = differenceInCalendarDays(new Date(), d);
    if (diffDays < 30) {
      return formatDistanceToNow(d, { addSuffix: true });
    }
    return format(d, 'dd MMM yyyy');
  } catch {
    return dateString;
  }
}

function formatChangeValue(key, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'price' || key === 'unit_price') {
    return formatQuoteMoney(value, 'INR');
  }
  if (key === 'tax_rate') return `${value}%`;
  if (key === 'template_key') {
    return String(value)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return String(value);
}

function formatChangeKey(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getChangeEntries(changes) {
  if (!changes || typeof changes !== 'object') return [];
  return Object.entries(changes).filter(
    ([key]) => !HIDDEN_CHANGE_KEYS.has(key),
  );
}

function ActivityChanges({ changes, compact = false }) {
  const entries = getChangeEntries(changes);
  if (!entries.length) return null;

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {entries.map(([key, diff]) => (
        <div
          key={key}
          className={`flex items-start gap-2.5 ${compact ? 'text-xs' : 'text-sm'} leading-snug min-w-0`}
        >
          <span
            className={`text-[#868686] shrink-0 ${compact ? 'w-[72px]' : 'w-[90px]'} truncate`}
          >
            {formatChangeKey(key)}
          </span>
          <span className="min-w-0 break-words">
            <span className="text-[#868686]">
              {formatChangeValue(key, diff?.old)}
            </span>
            <span className="mx-2 text-[#C5C5C5]">to</span>
            <span className="font-semibold text-[#1F2241]">
              {formatChangeValue(key, diff?.new)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function resolveActorLabel(item) {
  const name = (item.actor_name || '').trim();
  // Do not display email IDs as actor labels
  if (name && !name.includes('@')) return name;
  return '';
}

function resolveTitle(item) {
  if (item.title) return item.title;
  return (
    ACTION_LABEL[item.action_type] ||
    formatChangeKey(item.action_type) ||
    'Activity'
  );
}

function ActivityItem({ item, isLast, compact = false }) {
  const actorLabel = resolveActorLabel(item);
  const initial = (actorLabel || item.actor_name || item.actor_email || 'U')
    .replace(/^@/, '')
    .charAt(0)
    .toUpperCase();
  const timeAgo = formatTimeAgo(item.created_at);
  const title = resolveTitle(item);
  const changeEntries = getChangeEntries(item.changes);
  const showDescription =
    Boolean(item.description) && changeEntries.length === 0;

  return (
    <div
      className={`relative flex ${compact ? 'gap-3 pb-3' : 'gap-4 pb-5'} ${isLast ? '!pb-0' : ''}`}
    >
      {/* Avatar rail + connector */}
      <div
        className={`relative ${compact ? 'w-8' : 'w-10'} shrink-0 self-stretch`}
      >
        <div
          className={`relative z-10 ${
            compact ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'
          } rounded-full border-[1.5px] border-[#50AB3D] bg-white overflow-hidden flex items-center justify-center font-bold text-[#50AB3D]`}
        >
          {item.actor_avatar ? (
            <img
              src={item.actor_avatar}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            initial
          )}
        </div>
        {!isLast && (
          <div
            className={`absolute ${
              compact ? 'top-8 -bottom-3' : 'top-10 -bottom-5'
            } left-1/2 -translate-x-1/2 w-[2px] bg-[#50AB3D]`}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-start justify-between gap-4">
          <p
            className={`${
              compact ? 'text-xs' : 'text-[15px]'
            } font-semibold text-[#1F2241] leading-tight min-w-0`}
          >
            {title}
          </p>
          <span
            className={`${
              compact ? 'text-[10px]' : 'text-sm'
            } text-[#868686] shrink-0 whitespace-nowrap leading-tight pt-0.5`}
          >
            {timeAgo}
          </span>
        </div>
        {actorLabel ? (
          <p
            className={`text-[#868686] mt-0.5 truncate ${
              compact ? 'text-[11px]' : 'text-xs'
            }`}
          >
            {actorLabel}
          </p>
        ) : null}
        {showDescription && (
          <p
            className={`mt-1 text-[#868686] leading-relaxed ${
              compact ? 'text-xs' : 'text-sm'
            }`}
          >
            {item.description}
          </p>
        )}
        <ActivityChanges changes={item.changes} compact={compact} />
      </div>
    </div>
  );
}

/**
 * Quotation / product activity timeline.
 */
export function QuotationActivityTimeline({
  companyId,
  quotationId,
  productId,
  title = 'Activity',
  compact = false,
  pageSize = 20,
  className = '',
  scrollClassName = 'max-h-[360px]',
  hideCard = false,
}) {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useQuotationActivities(
    { companyId, quotationId, productId, pageSize },
    { enabled: !!companyId && !!quotationId },
  );

  const items = (data?.pages || []).flatMap((p) => p.results || []);

  const content = (
    <>
      {title && !hideCard && (
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#50AB3D] mb-3">
          {title}
        </p>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-3 animate-pulse py-1">
          {Array.from({ length: compact ? 3 : 4 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-[#EBF4EA] shrink-0" />
              <div className="flex-1 space-y-1.5 pt-1">
                <div className="h-3 w-3/4 bg-[#F5F5F5] rounded" />
                <div className="h-2.5 w-1/2 bg-[#F5F5F5] rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-4">
          <p className="text-xs text-red-500 font-medium">
            Failed to load activity
          </p>
          <p className="text-[11px] text-text-muted mt-1">
            {error?.response?.data?.detail ||
              error?.message ||
              'Please try again.'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 text-xs font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-text-muted text-center py-6 border border-dashed border-[#EBF4EA] rounded-xl">
          {productId
            ? 'No history for this product yet.'
            : 'No activity recorded yet.'}
        </p>
      ) : (
        <>
          <div
            className={`overflow-y-auto custom-scrollbar ${scrollClassName} pr-0.5`}
          >
            {items.map((item, idx) => (
              <ActivityItem
                key={item.id ?? `${item.action_type}-${item.created_at}-${idx}`}
                item={item}
                isLast={idx === items.length - 1 && !hasNextPage}
                compact={compact}
              />
            ))}
          </div>
          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mt-2 text-xs font-semibold text-primary hover:underline disabled:opacity-50 self-center"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </>
  );

  if (hideCard) {
    return (
      <div className={`flex flex-col min-w-0 ${className}`}>{content}</div>
    );
  }

  return (
    <div
      className={`bg-white rounded-2xl border border-border-default p-4 flex flex-col min-w-0 ${className}`}
    >
      {content}
    </div>
  );
}

export default QuotationActivityTimeline;
